/**
 * ChatBubble — the ONE chat bubble (TASK-711, dock consolidation).
 *
 * Markdown for assistant prose (fixes the KYC literal-asterisks bug class when
 * adopted), streaming caret, role styling. System messages render as a centered
 * chip.
 */

import React from 'react';
import { SimpleMarkdown } from '../ui/SimpleMarkdown';
import { sanitizeAssistantText } from '../../utils/sanitizeAssistantText';

export interface ChatBubbleProps {
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** Streaming in progress — shows the caret after the content. */
  streaming?: boolean;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({ role, content, streaming = false }) => {
  if (role === 'system') {
    return (
      <div className="flex justify-center" data-testid="chat-bubble-system">
        <span className="rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
          {content}
        </span>
      </div>
    );
  }

  const safeContent = sanitizeAssistantText(content);

  if (role === 'user') {
    return (
      <div className="flex justify-end" data-testid="chat-bubble-user">
        <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-primary/10 px-3.5 py-2 text-[13.5px] leading-relaxed text-foreground">
          <span className="whitespace-pre-wrap break-words">{content}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2.5" data-testid="chat-bubble-assistant">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-accent text-[13px] text-primary">
        ◆
      </div>
      <div className="max-w-[80%] rounded-2xl rounded-tl-sm bg-muted px-3.5 py-2 text-[13.5px] leading-relaxed text-foreground">
        <SimpleMarkdown content={safeContent} />
        {streaming && (
          <span
            className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-primary align-text-bottom"
            data-testid="streaming-caret"
          />
        )}
      </div>
    </div>
  );
};
